import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  Avatar,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp,
  CalendarToday,
  MonetizationOn,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import {
  useDividendCalendar,
  DividendCalendarEvent,
} from "../Charts/hooks/useDividendAnalysis";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const DividendCalendarView: React.FC = () => {
  const theme = useTheme();
  const currencySymbol = "$";
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const {
    data: calendarData,
    isLoading,
    isFetching,
    error,
  } = useDividendCalendar();

  // Group events by date
  const eventsByDate = useMemo(() => {
    if (!calendarData?.events) return new Map();

    const grouped = new Map();
    calendarData.events.forEach((event) => {
      const dateKey = dayjs(event.estimatedDate).format("YYYY-MM-DD");
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey).push(event);
    });
    return grouped;
  }, [calendarData?.events]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfWeek = endOfMonth.endOf("week");

    const days = [];
    let current = startOfWeek;

    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, "day")) {
      days.push(current);
      current = current.add(1, "day");
    }

    return days;
  }, [currentMonth]);

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
  };

  const goToNextMonth = () => {
    // Don't go beyond 1 year from now
    const oneYearFromNow = dayjs().add(1, "year");
    if (currentMonth.add(1, "month").isBefore(oneYearFromNow)) {
      setCurrentMonth((prev) => prev.add(1, "month"));
    }
  };

  // Get events for current month
  const monthlyEvents = useMemo(() => {
    if (!calendarData?.events) return [];
    return calendarData.events.filter((event) => {
      const eventDate = dayjs(event.estimatedDate);
      return (
        eventDate.year() === currentMonth.year() &&
        eventDate.month() === currentMonth.month()
      );
    });
  }, [calendarData?.events, currentMonth]);

  // Calculate monthly total
  const monthlyTotal = useMemo(() => {
    return monthlyEvents.reduce((sum, event) => sum + event.estimatedAmount, 0);
  }, [monthlyEvents]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mx: 3 }}>
        Failed to load dividend calendar: {error.message}
      </Alert>
    );
  }

  if (!calendarData) {
    return (
      <Alert severity="info" sx={{ mx: 3 }}>
        No dividend calendar data available
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Total Estimated Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.02)",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MonetizationOn sx={{ fontSize: 16, color: "#22c55e", display: "block" }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 500,
              }}
            >
              Total Estimated
            </Typography>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mt: 1,
            }}
          >
            {currencySymbol}
            {calendarData.stats.totalEstimatedIncome.toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            Next 12 months
          </Typography>
        </Box>

        {/* This Month Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.02)",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarToday sx={{ fontSize: 16, color: "#3b82f6", display: "block" }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 500,
              }}
            >
              {currentMonth.format("MMMM YYYY")}
            </Typography>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mt: 1,
            }}
          >
            {currencySymbol}
            {monthlyTotal?.toFixed(2) ?? "0.00"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            Estimated income
          </Typography>
        </Box>

        {/* Monthly Events Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.02)",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrendingUp sx={{ fontSize: 16, color: "#22c55e", display: "block" }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 500,
              }}
            >
              Payment Events
            </Typography>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mt: 1,
            }}
          >
            {monthlyEvents.length}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            This month
          </Typography>
        </Box>
      </Box>

      {/* Calendar */}
      <Card
        elevation={0}
        sx={{
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.02)",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Calendar Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <IconButton
              onClick={goToPreviousMonth}
              disabled={
                currentMonth.year() === dayjs().year() &&
                currentMonth.month() === dayjs().month()
              }
            >
              <ChevronLeft />
            </IconButton>

            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {currentMonth.format("MMMM YYYY")}
            </Typography>

            <IconButton
              onClick={goToNextMonth}
              disabled={currentMonth
                .add(1, "month")
                .isAfter(dayjs().add(1, "year"))}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Days of Week Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
              mb: 1,
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <Typography
                key={day}
                variant="body2"
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  color: "text.secondary",
                  p: 1,
                }}
              >
                {day}
              </Typography>
            ))}
          </Box>

          {/* Calendar Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
            }}
          >
            {calendarDays.map((day, index) => {
              const dateKey = day.format("YYYY-MM-DD");
              // Sort by amount descending so highest value dividends show first
              const dayEvents = (eventsByDate.get(dateKey) || []).sort(
                (a: DividendCalendarEvent, b: DividendCalendarEvent) =>
                  (b.estimatedAmount ?? 0) - (a.estimatedAmount ?? 0)
              );
              const isCurrentMonth =
                day.year() === currentMonth.year() &&
                day.month() === currentMonth.month();
              const isToday =
                day.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
              const isPast = day.isBefore(dayjs(), "day");

              return (
                <Box
                  key={index}
                  sx={{
                    minHeight: dayEvents.length === 1 ? 120 : 100, // Extra height for single dividend display
                    p: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    background: isToday
                      ? theme.palette.mode === "dark"
                        ? "rgba(34, 197, 94, 0.1)"
                        : "rgba(34, 197, 94, 0.05)"
                      : isCurrentMonth
                      ? "transparent"
                      : theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(0,0,0,0.02)",
                    opacity: isCurrentMonth ? 1 : 0.5,
                    position: "relative",
                  }}
                >
                  {/* Day Number */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday ? 700 : 500,
                      color: isToday
                        ? "#22c55e"
                        : isCurrentMonth
                        ? "text.primary"
                        : "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    {day.format("D")}
                  </Typography>

                  {/* Events */}
                  {dayEvents.length > 0 && (
                    <Box>
                      {dayEvents.length === 1 ? (
                        // Single dividend - expanded display with amount
                        <Tooltip
                          title={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {dayEvents[0].companyName}
                              </Typography>
                              <Typography variant="body2">
                                {currencySymbol}{dayEvents[0].estimatedAmount?.toFixed(2) ?? '0.00'}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {dayEvents[0].frequency}
                                {dayEvents[0].growthRate > 0 &&
                                  ` | +${dayEvents[0].growthRate?.toFixed(
                                    1
                                  ) ?? '0.0'}% growth`}
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                              p: 1,
                              borderRadius: 2,
                              backgroundColor: "#22c55e",
                              color: "white",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#16a34a",
                              },
                              transition: "background-color 0.2s ease",
                            }}
                          >
                            <Avatar
                              src={dayEvents[0].logo}
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: "rgba(255,255,255,0.2)",
                              }}
                            >
                              {dayEvents[0]?.ticker?.charAt(0) ?? "?"}
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                textAlign: "center",
                              }}
                            >
                              {dayEvents[0].ticker}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textAlign: "center",
                                background: "rgba(255,255,255,0.15)",
                                px: 0.5,
                                py: 0.2,
                                borderRadius: 1,
                              }}
                            >
                              {currencySymbol}{dayEvents[0].estimatedAmount?.toFixed(2) ?? '0.00'}
                            </Typography>
                            {dayEvents[0].growthRate > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.6rem",
                                  opacity: 0.8,
                                }}
                              >
                                +{dayEvents[0].growthRate?.toFixed(1) ?? '0.0'}%
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      ) : (
                        // Multiple dividends - compact display
                        <>
                          {dayEvents
                            .slice(
                              0,
                              dayEvents.length <= 3 ? dayEvents.length : 2
                            )
                            .map(
                              (
                                event: DividendCalendarEvent,
                                eventIndex: number
                              ) => (
                                <Tooltip
                                  key={eventIndex}
                                  title={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {event.companyName}
                                      </Typography>
                                      <Typography variant="body2">
                                        {currencySymbol}{event.estimatedAmount?.toFixed(2) ?? '0.00'}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {event.frequency}
                                        {event.growthRate > 0 &&
                                          ` | +${event.growthRate.toFixed(
                                            1
                                          )}% growth`}
                                      </Typography>
                                    </Box>
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 0.5,
                                      mb: 0.5,
                                      p: 0.5,
                                      borderRadius: 1,
                                      backgroundColor: "#22c55e",
                                      color: "white",
                                      fontSize: "0.65rem",
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: "#16a34a",
                                      },
                                      transition: "background-color 0.2s ease",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        flex: 1,
                                      }}
                                    >
                                      <Avatar
                                        src={event.logo}
                                        sx={{ width: 14, height: 14 }}
                                      >
                                        {event.ticker?.charAt(0) ?? '?'}
                                      </Avatar>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: "0.6rem",
                                          fontWeight: 600,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {event.ticker}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.6rem",
                                        fontWeight: 700,
                                        background: "rgba(255,255,255,0.2)",
                                        px: 0.5,
                                        py: 0.1,
                                        borderRadius: 0.5,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {currencySymbol}{event.estimatedAmount?.toFixed(2) ?? '0.00'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )
                            )}

                          {dayEvents.length > 3 && (
                            <Box
                              sx={{
                                textAlign: "center",
                                p: 0.5,
                                borderRadius: 1,
                                background:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.1)"
                                    : "rgba(0,0,0,0.05)",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.6rem",
                                  fontWeight: 600,
                                }}
                              >
                                +{dayEvents.length - 2} more
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "#22c55e",
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                }}
                              >
                                {currencySymbol}
                                {dayEvents
                                  .slice(2)
                                  .reduce(
                                    (sum: number, e: DividendCalendarEvent) =>
                                      sum + (e.estimatedAmount ?? 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DividendCalendarView;
